import { useState, type FormEvent } from 'react';
import { ErrorState, Loading } from '../../components/States';
import { useAdminCoupons, useAdminProducts, useCreateAdminCoupon, useUpdateAdminCouponStatus } from '../../hooks/api-hooks';
import { formatDateTime, formatPrice } from '../../lib/format';

export function AdminCouponsPage() {
  const coupons = useAdminCoupons();
  const courses = useAdminProducts('COURSE');
  const create = useCreateAdminCoupon();
  const update = useUpdateAdminCouponStatus();
  const [code, setCode] = useState('');
  const [discountType, setDiscountType] = useState<'PERCENT' | 'FIXED'>('PERCENT');
  const [discountValue, setDiscountValue] = useState('10');
  const [minimumOrderAmount, setMinimumOrderAmount] = useState('0');
  const [totalUsageLimit, setTotalUsageLimit] = useState('');
  const [usageLimitPerUser, setUsageLimitPerUser] = useState('1');
  const [courseIds, setCourseIds] = useState<string[]>([]);

  function toggleCourse(id: string) {
    setCourseIds((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id]);
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    create.mutate({
      code: code.trim().toUpperCase(),
      discountType,
      discountValue: discountType === 'PERCENT' ? Math.round(Number(discountValue) * 100) : Number(discountValue),
      minimumOrderAmount: Number(minimumOrderAmount || 0),
      totalUsageLimit: totalUsageLimit ? Number(totalUsageLimit) : undefined,
      usageLimitPerUser: usageLimitPerUser ? Number(usageLimitPerUser) : undefined,
      courseIds,
    }, {
      onSuccess: () => { setCode(''); setCourseIds([]); },
    });
  }

  return (
    <section className="admin-page">
      <header className="admin-page-header"><div><h1>Voucher</h1><p>Tạo mã giảm giá và giới hạn theo một hoặc nhiều khóa học.</p></div></header>
      <details className="admin-create-panel">
        <summary>Tạo voucher mới</summary>
        <form className="admin-form" onSubmit={submit}>
          <div className="admin-form-grid">
            <label>Mã voucher<input required maxLength={64} value={code} onChange={(event) => setCode(event.target.value.toUpperCase())} /></label>
            <label>Loại giảm<select value={discountType} onChange={(event) => setDiscountType(event.target.value as 'PERCENT' | 'FIXED')}><option value="PERCENT">Phần trăm</option><option value="FIXED">Số tiền cố định</option></select></label>
            <label>{discountType === 'PERCENT' ? 'Mức giảm (%)' : 'Mức giảm (VND)'}<input required min="1" max={discountType === 'PERCENT' ? '100' : undefined} type="number" value={discountValue} onChange={(event) => setDiscountValue(event.target.value)} /></label>
            <label>Đơn tối thiểu (VND)<input min="0" step="1000" type="number" value={minimumOrderAmount} onChange={(event) => setMinimumOrderAmount(event.target.value)} /></label>
            <label>Tổng lượt dùng<input min="1" type="number" value={totalUsageLimit} onChange={(event) => setTotalUsageLimit(event.target.value)} placeholder="Không giới hạn" /></label>
            <label>Lượt dùng mỗi học viên<input min="1" type="number" value={usageLimitPerUser} onChange={(event) => setUsageLimitPerUser(event.target.value)} /></label>
          </div>
          <fieldset className="admin-choice-list"><legend>Áp dụng cho khóa học</legend>
            {courses.data?.map((course) => <label key={course.courseId}><input type="checkbox" checked={Boolean(course.courseId && courseIds.includes(course.courseId))} onChange={() => course.courseId && toggleCourse(course.courseId)} />{course.title}</label>)}
          </fieldset>
          {!courseIds.length && <p className="form-help">Chọn ít nhất một khóa học.</p>}
          {create.error && <ErrorState error={create.error} />}
          <button disabled={create.isPending || !courseIds.length}>{create.isPending ? 'Đang tạo…' : 'Tạo voucher'}</button>
        </form>
      </details>

      {coupons.isPending && <Loading label="Đang tải voucher…" />}
      {coupons.error && <ErrorState error={coupons.error} />}
      {coupons.data && <div className="admin-table-wrap"><table className="admin-table">
        <thead><tr><th>Mã</th><th>Mức giảm</th><th>Khóa học</th><th>Đã dùng</th><th>Ngày tạo</th><th>Trạng thái</th></tr></thead>
        <tbody>{coupons.data.map((coupon) => <tr key={coupon.id}>
          <td><strong>{coupon.code}</strong></td>
          <td>{coupon.discountType === 'PERCENT' ? `${Number(coupon.discountValue) / 100}%` : formatPrice(Number(coupon.discountValue))}</td>
          <td><span className="admin-tags">{coupon.courses.map((course) => <small key={course.id}>{course.title}</small>)}</span></td>
          <td>{coupon.usageCount}{coupon.totalUsageLimit ? ` / ${coupon.totalUsageLimit}` : ''}</td>
          <td>{formatDateTime(coupon.createdAt)}</td>
          <td><button type="button" className={`status-button ${coupon.status === 'ACTIVE' ? 'published' : ''}`} disabled={update.isPending} onClick={() => update.mutate({ id: coupon.id, status: coupon.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE' })}>{coupon.status === 'ACTIVE' ? 'Đang dùng' : 'Đã tắt'}</button></td>
        </tr>)}{!coupons.data.length && <tr><td colSpan={6} className="admin-empty">Chưa có voucher.</td></tr>}</tbody>
      </table></div>}
    </section>
  );
}
